import React, { useEffect, useRef, useState } from "react";
import images from "../../assets/images";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import Modal from "../../components/modal/Modal";
import { useRoleManageStore } from "../../store/store";
import {
  generateEmptyPermissions,
  proxyUrl,
} from "../../common/utils";
import { RolePermissions, SeedModule } from "../../types/api";
import { useAuth } from "../../auth/AuthProvider";
import { usePermissionSeedStore } from "../../store/usePermissionSeedStore";

const COLORS = {
  create: "text-green-600",
  read: "text-blue-600",
  update: "text-yellow-600",
  delete: "text-red-600",
};

const validUserTypes = [
  { value: "orgmanager", label: "Manager" },
  { value: "orguser", label: "User" },
];

const staffCategoryOptions = [
  { value: "", label: "Not set / General" },
  { value: "housekeeping", label: "Housekeeping" },
  { value: "maintenance", label: "Maintenance" },
  { value: "admin", label: "Admin" },
];

const RoleManagement: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [roleName, setRoleName] = useState<string>("");
  const [staffCategory, setStaffCategory] = useState<
    "" | "housekeeping" | "maintenance" | "admin"
  >("");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState(false);

  const { user } = useAuth();
  const [seedData, setSeedData] = useState<SeedModule[]>([]);
  const [permissions, setPermissions] = useState<RolePermissions>({});
  const [selectedModuleKey, setSelectedModuleKey] = useState<string>("");
  const { rolesData, fetchAllRoles, createRole, updateRole } =
    useRoleManageStore();
  useEffect(() => {
    fetchAllRoles();

    const storeSeed = usePermissionSeedStore.getState().seed;
    if (storeSeed.length > 0) {
      setSeedData(storeSeed);
      setSelectedModuleKey(storeSeed[0].key);
    }
  }, []);

  useEffect(() => {
    const fetchRole = async () => {
      // const roleRes = await fetch(`/api/roles/${selectedRole}`);
      // const roleData = await roleRes.json();
      //// use state context !!!
      const rolesRes = rolesData.find((r) => r._id === selectedRole);
      const normalized: RolePermissions = {};
      for (const [mod, acts] of Object.entries(rolesRes?.permissions || {})) {
        const actionsArray = Array.isArray(acts) ? acts : [];
        normalized[mod] = new Set(actionsArray);
      }
      setPermissions(normalized);
      setStaffCategory(rolesRes?.staffCategory || "");
    };

    if (selectedRole) fetchRole();
  }, [selectedRole]);

  const toggle = (subModuleKey: string, act: string) => {
    setPermissions((prev) => {
      const next = { ...prev };
      next[subModuleKey] = new Set(next[subModuleKey] || []);
      if (next[subModuleKey].has(act)) next[subModuleKey].delete(act);
      else next[subModuleKey].add(act);
      return next;
    });

    if (roleName === "") {
      const roleData =
        rolesData && rolesData.find((r) => r._id === selectedRole);
      setRoleName(roleData?.roleName || "");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const flattenPermissions = (
      input: RolePermissions
    ): Record<string, string[]> =>
      Object.fromEntries(
        Object.entries(input).map(([mod, set]) => [mod, Array.from(set)])
      );

    const payload = {
      roleName,
      staffCategory,
      permissions:
        selectedRole && isEditMode
          ? flattenPermissions(permissions ?? {})
          : generateEmptyPermissions(seedData),
    };

    if (selectedRole && isEditMode) {
      await updateRole(selectedRole, payload);
      setIsEditMode(false);
    } else {
      const createdId = await createRole(payload);
      setSelectedRole(createdId);
    }

    setIsOpen(false);
    setRoleName("");
  };

  const handleSavePerm = async () => {
    if (!selectedRole) {
      alert("Please select a role before saving permissions.");
      return;
    }

    const flattenPermissions = (
      input: RolePermissions
    ): Record<string, string[]> =>
      Object.fromEntries(
        Object.entries(input).map(([subModuleKey, actionSet]) => [
          subModuleKey,
          Array.from(actionSet),
        ])
      );

    const payload = {
      roleName,
      staffCategory,
      permissions: flattenPermissions(permissions ?? {}),
    };

    if (Object.keys(payload.permissions).length === 0) {
      alert("No permissions to update.");
      return;
    }

    await updateRole(selectedRole, payload);
    alert("Permissions updated.");
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = e.target.value;
    setSelectedRole(roleId);

    const selected = rolesData.find((r: any) => r._id === roleId);
    if (selected) {
      setRoleName(selected.roleName);
      setStaffCategory(selected.staffCategory || "");
    } else {
      setRoleName("");
      setStaffCategory("");
    }
  };

  const tabRef = useRef<HTMLUListElement>(null);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabRef.current) {
      const scrollAmount = 200;
      tabRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const scrollToTab = (index: number) => {
    const ul = tabRef.current;
    if (ul && index > 2) {
      const tabEl = ul.children[index] as HTMLElement;
      if (tabEl) {
        ul.scrollTo({
          left: tabEl.offsetLeft - 1,
          behavior: "smooth",
        });
      }
    }
  };

  const isAdmin =
    user?.roles.includes("superadmin") || user?.roles.includes("orgadmin");

  return (
    <>
      <div className="px-2 py-4 mt-14 ml-2 min-h-screen">
        <div className="flex justify-between items-center">
          <h2 className="">Role Management</h2>
          {/* <div className="dashHeaderRight">
            <div className="tableSearchBlock d-flex justify-content-between p-0"> */}
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-3 py-2 rounded-full"
            onClick={() => {
              setIsOpen(true);
              setRoleName("");
              setStaffCategory("");
              setIsEditMode(false);
            }}
          >
            <img src={images.createRoleImg} width={20} alt="create-role" />
            CREATE ROLE
          </button>
          {/* </div>
          </div> */}
        </div>
        <div className="tableSearchBlock d-flex justify-content-between p-0 mt-2">
          {/* <div className="leftTableSearchBlock d-flex"> */}
          <div className="flex items-center m-0 bg-white gap-3 border-2 border-[#a5b7cf] rounded-lg px-3 py-2 min-w-64 relative">
            <select
              className="px-3 bg-transparent text-[#61748e] text-md font-medium outline-none flex-1 appearance-none"
              onChange={handleRoleChange}
              value={selectedRole}
            >
              <option value="">Select Role</option>
              {rolesData.length > 0 ? (
                rolesData.map((role: any, index) => (
                  <option key={index} value={role._id}>
                    {role.roleName}
                  </option>
                ))
              ) : (
                <option disabled>No roles available</option>
              )}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-500 absolute right-4 pointer-events-none" />
          </div>
          {/* </div> */}
          {selectedRole !== "" && (
            <div className="rightTableSearchBlock d-flex">
              <button
                className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-full roleEditBt roundedBt iconWithBt btn-sm btn-gray text-uppercase"
                onClick={() => {
                  setIsOpen(true);
                  // setRoleName(selectedRole);
                  setIsEditMode(true);
                  const role =
                    rolesData && rolesData.find((r) => r._id === selectedRole);
                  setRoleName(role?.roleName || "");
                  setStaffCategory(role?.staffCategory || "");
                }}
              >
                <img src={images.editIcon} width={20} alt="edit-role" />
                Edit
              </button>
            </div>
          )}
        </div>
        {selectedRole !== "" ? (
          <div className="tab-container">
            <div className="overflow-hidden relative">
              <ul
                ref={tabRef}
                className="nav nav-tabs tab-scroll flex flex-nowrap overflow-x-auto whitespace-nowrap gap-2 no-scrollbar scroll-smooth mx-5"
                id="myTab"
                role="tablist"
              >
                {seedData.map((module, index) => {
                  const moduleKey = module.key || `module-${index}`;
                  return (
                    <li
                      className="nav-item nav-item1"
                      key={moduleKey}
                      role="presentation"
                    >
                      <button
                        id={`tab-${index}`}
                        className={`nav-link ${
                          selectedModuleKey === moduleKey ? "active" : ""
                        }`}
                        onClick={() => {
                          setSelectedModuleKey(moduleKey);
                          scrollToTab(index);
                        }}
                        role="tab"
                        aria-selected={selectedModuleKey === moduleKey}
                      >
                        {module.name.toUpperCase()}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-[#f8f8fa] px-2"
                onClick={() => scrollTabs("left")}
              >
                <ChevronLeft size={25} />
              </button>
              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-[#f8f8fa] px-2"
                onClick={() => scrollTabs("right")}
              >
                <ChevronRight size={25} />
              </button>
            </div>
            <div className="tab-content mt-2">
              {(() => {
                const selectedModule = seedData.find(
                  (mod) => mod.key === selectedModuleKey
                );

                if (
                  !selectedModule ||
                  selectedModule?.subModules?.length === 0
                ) {
                  return (
                    <div className="fallback-ui">
                      <h3 className="text-gray-800 font-semibold text-base">
                        No submodules available.
                      </h3>
                    </div>
                  );
                }

                return (
                  <>
                    <span className="flex justify-end items-center px-2 pb-2">
                      * C-Create, R-Read, U-Update, D-Delete
                    </span>
                    <div
                      key={selectedModule.key}
                      className="tab-pane fade show active"
                      role="tabpanel"
                    >
                      <div className="space-y-3">
                        {selectedModule.subModules.map((subModule, index) => (
                          <div
                            key={index}
                            className="submodule-item roleCard flex justify-between items-center px-3 py-2 rounded shadow-sm bg-white"
                          >
                            <h3 className="text-gray-800 font-semibold text-base">
                              {subModule.name}
                            </h3>
                            <div className="roleCheckboxRight flex">
                              {subModule.actions.map((action) => {
                                const disabled =
                                  subModule.adminOnly?.includes(action) &&
                                  !isAdmin;
                                const readonly =
                                  subModule.readonly?.includes(action);
                                return (
                                  <div
                                    key={action}
                                    className="checkbox flex items-center py-1 me-1"
                                  >
                                    <input
                                      type="checkbox"
                                      disabled={disabled}
                                      readOnly={readonly}
                                      checked={
                                        permissions?.[subModule.key]?.has(
                                          action
                                        ) || false
                                      }
                                      onChange={() =>
                                        toggle(subModule.key, action)
                                      }
                                      id={`${subModule.key}_${action}`}
                                      className="form-checkbox h-4 w-4 cursor-pointer"
                                    />
                                    <label
                                      htmlFor={`${subModule.key}_${action}`}
                                      className={`ml-2 font-semibold ${
                                        readonly
                                          ? "text-gray-500"
                                          : "text-black"
                                      }`}
                                    >
                                      {action.charAt(0).toUpperCase()}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-end">
                          <button
                            onClick={handleSavePerm}
                            className="float-right roleEditBt mt-2 px-4 py-2 bg-[#3F65AB] text-white rounded shadow hover:bg-[#2d4a81]"
                          >
                            Save Permissions
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col align-items-center py-5 my-5">
              <h4 className="font-semibold text-gray-500 pt-4 pr-4">
                Select Role
              </h4>
              <img src={images.selectAssetsImg} width={165} alt="edit-role" />
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-xl rounded-xl relative"}
      >
        {
          <>
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">
                {/* {selectedRole ? "Edit Role":"Create Role"} */}
                {"Role Details"}
              </h2>
              <div className="pr-5 mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name
                </label>
                <input
                  type="text"
                  name="roleName"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="Enter Role Name"
                  className="w-75 px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                />
              </div>
              <div className="pr-5 mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Staff Category
                </label>
                <select
                  name="staffCategory"
                  value={staffCategory}
                  onChange={(e) =>
                    setStaffCategory(
                      e.target.value as
                        | ""
                        | "housekeeping"
                        | "maintenance"
                        | "admin",
                    )
                  }
                  className="w-75 px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                >
                  {staffCategoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Controls workflow behavior in modules like Washroom
                  Management (e.g. who shows up as assignable maintenance
                  staff). This is separate from the C/R/U/D permissions
                  below.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-3 p-2 bg-[#5A7192] w-full rounded-b-lg">
              <button
                onClick={() => {
                  setIsOpen(false);
                  isEditMode && setIsEditMode(false);
                }}
                className="bg-[#8497B0] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
              >
                CANCEL
              </button>
              <button
                onClick={handleSubmit}
                className="bg-[#00A8A8] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
              >
                SUBMIT
              </button>
            </div>
          </>
        }
      </Modal>
    </>
  );
};

export default RoleManagement;
